import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {Controller, useForm} from 'react-hook-form';

import Card from 'components/Card';
import Button from 'components/Button';
import TextField from 'components/TextField';
import CardContent from 'components/CardContent';
import Logo from 'components/Logo';
import Typography from 'components/Typography';
import Select from 'components/Select';
import {validateEmail} from 'utils/validators';
import useAuth from 'hooks/useAuth';
import EyeIcon from '../EmployeeForm/icons/eye.svg';
import EyeDisabledIcon from '../EmployeeForm/icons/eye-disable.svg';
import styles from './Signup.module.scss';
import toast, {Toaster} from 'react-hot-toast';
import api from 'api';
import {get} from 'lodash';
import ConfirmationModal from './ConfirmationModal';
import {CompanyType} from 'types';

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  company_name: string;
  company_type: {
    label: string;
    value: CompanyType;
  };
}

const companyTypeOptions = [
  {label: 'Service provider', value: 'service_provider'},
  {label: 'Property manager', value: 'property_manager'},
  {label: 'Delivery manager', value: 'delivery_manager'},
];

function Signup() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    control,
    formState: {errors},
  } = useForm<FormValues>();

  const {register: signup} = useAuth();

  async function submit(data: FormValues) {
    setLoading(true);

    try {
      await signup({...data, company_type: data.company_type.value});
      toast.success('Email confirmed');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const confirmHandler = async (value: string) => {
    try {
      await api.post('/companies/verify-code/', {
        code: value,
        email: getValues('email'),
      });
      handleSubmit(submit)();
      setLoading(false);
      setConfirmationVisible(false);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
      setLoading(false);
    }
  };

  const sendConfirmation = async () => {
    try {
      await api.post('/companies/send-code/', {
        email: getValues('email'),
      });
      toast.success('Confirmation code sent to your email');
      setConfirmationVisible(true);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
    }
  };

  const resendCode = () => {
    sendConfirmation();
  };

  return (
    <Card className={styles.root}>
      <CardContent pt="none">
        <Logo />
        <Typography variant="h2" textAlign="center">
          Sign Up
        </Typography>
        <Fragment>
          <form onSubmit={handleSubmit(submit)}>
            <TextField
              label="Company Name"
              placeholder="Company Name"
              error={errors.company_name?.message}
              {...register('company_name', {
                required: 'Required',
              })}
            />
            <Controller
              control={control}
              rules={{required: 'Required'}}
              name="company_type"
              render={({field}) => (
                <Fragment>
                  <div className={styles.label}>Company Type</div>
                  <Select
                    isSearchable={false}
                    placeholder={
                      <span className={styles.selectPlaceholder}>
                        Select company type
                      </span>
                    }
                    options={companyTypeOptions}
                    error={errors.company_type?.message}
                    {...field}
                  />
                  {errors.company_type?.message && (
                    <Typography className={styles.error} variant="p">
                      {errors?.company_type?.message}
                    </Typography>
                  )}
                </Fragment>
              )}
            />

            <TextField
              label="Email"
              placeholder="Email"
              type="email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Required',
                validate: validateEmail,
              })}
            />
            <TextField
              label="First Name"
              placeholder="First Name"
              error={errors.first_name?.message}
              {...register('first_name', {
                required: 'Required',
              })}
            />
            <TextField
              label="Last Name"
              placeholder="Last Name"
              error={errors.last_name?.message}
              {...register('last_name', {
                required: 'Required',
              })}
            />

            <div style={{position: 'relative'}}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                error={errors.password?.message}
                {...register('password', {
                  required: 'Required',
                  minLength: {value: 6, message: 'Too short'},
                })}
              />
              <img
                onClick={() => setShowPassword((pr) => !pr)}
                className={styles.eyeIcon}
                src={showPassword ? EyeIcon : EyeDisabledIcon}
                alt="eye"
              />
            </div>
            <div style={{position: 'relative'}}>
              <TextField
                label="Confirm Password"
                type={showPassword1 ? 'text' : 'password'}
                placeholder="Confirm Password"
                error={errors.passwordConfirmation?.message}
                {...register('passwordConfirmation', {
                  required: 'Required',
                  validate: (val: string) => {
                    if (watch('password') !== val) {
                      return 'Your passwords do no match';
                    }
                  },
                })}
              />
              <img
                onClick={() => setShowPassword1((pr) => !pr)}
                className={styles.eyeIcon}
                src={showPassword1 ? EyeIcon : EyeDisabledIcon}
                alt="eye"
              />
            </div>
            <div className={styles.button_container}>
              <Button
                onClick={handleSubmit(sendConfirmation)}
                loading={loading}
              >
                Sign Up
              </Button>
            </div>
          </form>
          <div className={styles.signup}>
            <Typography variant="p">
              Already have an account?{' '}
              <Link className={styles.link} to="/signin">
                Sign In
              </Link>
            </Typography>
          </div>
        </Fragment>
      </CardContent>
      <Toaster />
      <ConfirmationModal
        email={getValues('email')}
        confirmHandler={confirmHandler}
        resendCode={resendCode}
        confirmationVisible={confirmationVisible}
        onClose={() => setConfirmationVisible(false)}
      />
    </Card>
  );
}

export default Signup;
